import React, { useState, useEffect } from 'react';
import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@material-ui/core';
import BackIcon from '@material-ui/icons/ReplyOutlined';
import { useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import DatePicker, { registerLocale } from 'react-datepicker';
import { es, enUS } from 'date-fns/locale';
import {
  getSurveyResponseById,
  putSurveyResponse,
} from '../../../requests/api/survey-response';
import useStyles from './styles';
import 'react-datepicker/dist/react-datepicker.css';
import Snackbar from '../../../components/Snackbar';
import { parseCompleteDate, getDuration, shouldHighlightDuration } from '../../../utils/date';

registerLocale('es', es);
registerLocale('en', enUS);

const EditResponse = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const shortLocale = i18n.language ? i18n.language.slice(0, 2) : 'es';
  const { taskResponseId } = useParams();
  const history = useHistory();
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('');

  const [surveyResponse, setSurveyResponse] = useState({});
  const [responseDetail, setResponseDetail] = useState({
    surveyResponseId: null,
    locationName: null,
    taskName: null,
    type: null,
    category: null,
    userName: null,
    userEmail: null,
    startedAt: null,
    finishedAt: null,
    label: null,
    createdAt: null,
    startedAtReal: null,
    deleted: null,
    reviewStatus: null,
  });

  const columnLabelAndKey = [
    {
      key: 'surveyResponseId',
      label: t('detailResponse.tableHeaders.surveyResponseId'),
    },
    {
      key: 'locationName',
      label: t('detailResponse.tableHeaders.locationName'),
    },
    { key: 'taskName', label: t('detailResponse.tableHeaders.taskName') },
    { key: 'type', label: t('detailResponse.tableHeaders.type') },
    { key: 'category', label: t('detailResponse.tableHeaders.category') },
    { key: 'userName', label: t('detailResponse.tableHeaders.userName') },
    { key: 'userEmail', label: t('detailResponse.tableHeaders.userEmail') },
    { key: 'startedAt', label: t('detailResponse.tableHeaders.startedAt') },
    { key: 'finishedAt', label: t('detailResponse.tableHeaders.finishedAt') },
    {
      key: 'measurementTime',
      label: t('detailResponse.tableHeaders.measurementTime'),
    },
    { key: 'createdAt', label: t('detailResponse.tableHeaders.createdAt') },
    { key: 'startedAtReal', label: t('detailResponse.tableHeaders.startedAtReal') },
    { key: 'deleted', label: t('detailResponse.tableHeaders.status') },
  ];

  useEffect(() => {
    getSurveyResponseById(taskResponseId).then((response) => {
      if (response.success) {
        setSurveyResponse(response.data.data);
      }
    });
  }, [taskResponseId, history]);

  useEffect(() => {
    if (surveyResponse) {
      setResponseDetail({
        surveyResponseId: surveyResponse.id,
        locationName: `${surveyResponse.TaskInstance?.Location?.chain || ''} - ${surveyResponse.TaskInstance?.Location?.chainSubCategory || ''}`,
        taskName: surveyResponse.Survey?.title,
        type: surveyResponse.Survey?.type,
        category: surveyResponse.categoryCode,
        userName: surveyResponse.TaskInstance?.User?.fullName,
        userEmail: surveyResponse.TaskInstance?.User?.email,
        startedAt: surveyResponse.startedAt
          ? new Date(surveyResponse.startedAt)
          : null,
        finishedAt: surveyResponse.finishedAt
          ? new Date(surveyResponse.finishedAt)
          : null,
        label: surveyResponse?.Survey?.label,
        createdAt: surveyResponse.createdAt
          ? new Date(surveyResponse.createdAt)
          : null,
        startedAtReal: parseCompleteDate(surveyResponse.startedAtReal),
        deleted: surveyResponse.deleted,
        reviewStatus: surveyResponse.SurveyResponseReview?.status,
      });
    }
  }, [surveyResponse]);

  const handleSave = async () => {
    const body = {
      startedAt: responseDetail.startedAt,
      finishedAt: responseDetail.finishedAt,
      createdAt: responseDetail.createdAt,
    };
    const resp = await putSurveyResponse(responseDetail.surveyResponseId, body);
    if (resp.success) {
      setSeverity('success');
      setMessage(t('common.savedSuccessfully'));
    } else {
      setSeverity('error');
      setMessage(t('common.savedUnsuccessfully'));
    }
  };

  /**
   * Parsea el valor de la respuesta
   * @param {*} key - key de la respuesta
   * @returns valor de la respuesta
   */
  const parseValue = (key) => {
    if (key === 'measurementTime') {
      return (
        getDuration(responseDetail.startedAt, responseDetail.finishedAt) || '-'
      );
    }
    const value = responseDetail[key];
    if (value === null || value === undefined) return '-';
    if (key === 'deleted') {
      if (value) return t('status.deleted');
      if (responseDetail.reviewStatus === 'rejected') return t('qa.status.rejected');
      return t('status.active');
    }
    return value;
  };

  const highlightMeasurementTime = shouldHighlightDuration(
    responseDetail.startedAt,
    responseDetail.finishedAt,
    responseDetail.label,
  );

  const setSelectedDate = (date, key) => {
    setResponseDetail({
      ...responseDetail,
      [key]: date,
    });
  };

  const isValidDate = (date) => date && date instanceof Date && !Number.isNaN(date.getTime());

  const isDateField = (key) => key === 'startedAt' || key === 'finishedAt' || key === 'createdAt';

  const toPickerUTC = (date) => {
    if (!date) return null;
    const offset = date.getTimezoneOffset();
    return new Date(date.getTime() + offset * 60 * 1000);
  };

  const fromPickerUTC = (date) => {
    if (!date) return null;
    const offset = date.getTimezoneOffset();
    return new Date(date.getTime() - offset * 60 * 1000);
  };

  const handleChange = (newDate, key) => {
    const date = fromPickerUTC(newDate);
    setSelectedDate(date, key);
  };

  const renderDatePicker = (item) => {
    let date = responseDetail[item.key];
    if (!isValidDate(date)) {
      return parseValue(item.key);
    }
    date = toPickerUTC(date);
    return (
      <div style={{ minWidth: '200px' }}>
        <DatePicker
          selected={date}
          onChange={(newDate) => handleChange(newDate, item.key)}
          showTimeSelect
          timeFormat="HH:mm:ss"
          timeIntervals={15}
          dateFormat="dd-MM-yyyy HH:mm:ss"
          className={classes.datePicker}
          locale={shortLocale}
          timeCaption={t('common.time')}
        />
      </div>
    );
  };

  const backHandle = () => {
    const from = location.state?.from;
    const backUrl = '/responses';

    if (from) {
      history.push(from);
    } else {
      history.push(backUrl);
    }
  };

  const backButton = (
    <IconButton
      id="backButton"
      aria-label="back"
      className={classes.backButtonQuestionResponse}
      onClick={() => backHandle()}
    >
      <BackIcon fontSize="small" />
      <span>{t('groups.actionButtons.back')}</span>
    </IconButton>
  );

  return (
    <>
      <div className={classes.content}>
        <div className={classes.headerContainer}>
          <div className={classes.header}>
            {backButton}
            <Typography variant="h1" className={classes.title} gutterBottom>
              {t('editResponseView.title')}
            </Typography>
          </div>
          <div className={classes.buttonsContainer}>
            <Button
              onClick={() => history.push(`/responses/${taskResponseId}`)}
            >
              {t('editResponseView.detailButton')}
            </Button>
          </div>
          <div className={classes.buttonsContainer}>
            <Button onClick={() => handleSave()}>
              {t('task.editDetail.save')}
            </Button>
          </div>
        </div>
        <TableContainer component={Paper}>
          <Table className={classes.table}>
            <TableBody>
              {columnLabelAndKey.map((item) => (
                <TableRow key={item.key}>
                  <TableCell component="th" scope="row">
                    <Typography className={classes.semiBold}>
                      {item.label}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {(() => {
                      if (isDateField(item.key)) {
                        return renderDatePicker(item);
                      }
                      if (item.key === 'measurementTime') {
                        return (
                          <Typography
                            style={{
                              // '#d32f2f' is exactly MUI's `error.dark`.
                              color: highlightMeasurementTime
                                ? theme.palette.error.dark
                                : 'inherit',
                              fontWeight: highlightMeasurementTime
                                ? 600
                                : 400,
                            }}
                          >
                            {parseValue(item.key)}
                          </Typography>
                        );
                      }
                      return parseValue(item.key);
                    })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <div className={classes.extraSpace} />
      </div>
      <Snackbar
        open={message !== ''}
        message={message}
        severity={severity}
        onClose={() => setMessage('')}
      />
    </>
  );
};

export default EditResponse;
